import axios from "axios";
import { ModalContext } from "app/app";
import { Fragment, useContext, useState } from 'react';

import { Form, Formik } from 'formik';

import { FieldInput } from "components/fieldInput";
import { useNavigate, ViewContext } from "components/lib";

import styles from "./styles.module.scss";
import { useEffect } from "react";
import { validationSchema } from "./validationSchema";

export function ServiceModal() {
  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const { modal, setModal } = useContext(ModalContext);

  const quoteType = ["Cover Whale", "Progressive", "Nirvana"];
  const [selectedQuote, setSelectedQuote] = useState("");
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [quoteNumberValue, setQuoteNumberValue] = useState("");
  const [agencyNames, setAgencyNames] = useState({});
  const [companyIds, setCompanyIds] = useState({});
  const [companyEmails, setCompanyEmails] = useState({});
  const [companyNames, setCompanyNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [manualInputRequired, setManualInputRequired] = useState(false);

  const fetchAllCompanies = async () => {
    try {
      const response = await axios.get("/api/company/all");
      console.log("All Companies:", response.data.data);

      const companies = response.data.data;
      setCompaniesOptions(
        companies.map((company) => ({
          label: company.name,
          value: company.name,
        }))
      );

      const emails = companies.reduce((acc, company) => {
        acc[company.name] = company.email;
        return acc;
      }, {});

      const ids = companies.reduce((acc, company) => {
        acc[company.name] = company.id;
        return acc;
      }, {});

      const names = companies.reduce((acc, company) => {
        acc[company.name] = company.name;
        return acc;
      }, {});

      setCompanyEmails(emails);
      setCompanyIds(ids);
      setCompanyNames(names);
    } catch (error) {
      console.error("Error fetching all companies:", error);
      context.notification.show(
        "Failed to load companies",
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  };

  const fetchAllAccounts = async () => {
    try {
      const response = await axios.get("/api/account/all/minimal");
      console.log("All Accounts:", response.data.data);

      const agencies = response.data.data;
      setAgenciesOptions(
        agencies.map((agency) => ({
          label: agency.name,
          value: agency.id,
        }))
      );

      const agencyNames = agencies.reduce((acc, agency) => {
        acc[agency.id] = agency.name;
        return acc;
      }, {});

      setAgencyNames(agencyNames);
    } catch (error) {
      console.error("Error fetching all accounts:", error);
      context.notification.show(
        "Failed to load agencies",
        "error",
        true,
        "toast",
        "x-circle"
      );
    }
  };

  useEffect(() => {
    fetchAllCompanies();
    fetchAllAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [companyInfo, setCompanyInfo] = useState({
    companyId: "",
    companyName: "",
    companyEmail: "",
  });

  const [agencyInfo, setAgencyInfo] = useState({
    agencyId: "",
    agencyName: "",
  });

  const handleCompanyChange = (company) => {
    const companyId = Array.isArray(company) ? company[0].value : company.value;
    setCompanyInfo({
      companyId: companyIds[companyId],
      companyName: companyNames[companyId],
      companyEmail: companyEmails[companyId],
    });
  };

  const handleAgencyChange = (agency) => {
    const agencyId = Array.isArray(agency) ? agency[0].value : agency.value;
    setAgencyInfo({
      agencyId: agencyId,
      agencyName: agencyNames[agencyId],
    });
  };

  const fetchAgencyData = async (agencyId) => {
    try {
      const response = await axios.get(`/api/account/${agencyId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching agency data:', error);
      return null;
    }
  };

  const fetchQuoteFromCoverWhale = async (submissionNumber) => {
    try {
      const response = await axios.get(`/api/quote/info/${submissionNumber}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching from CW API:', error);
      return null;
    }
  };

  const findMostRecentValidQuote = (quotes) => {
    if (!quotes || quotes.length === 0) return null;

    const sortedQuotes = [...quotes].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    return sortedQuotes.find(quote =>
      quote.account_name &&
      quote.company_name &&
      quote.quote &&
      quote.quote.retailAgent
    );
  };

  const submitTicket = async (ticketData) => {
    try {
      const response = await axios.post("/api/tickets", ticketData);
      if (response.status === 201) {
        const ticketId = response.data.data.id;
        setModal("");
        setSelectedQuote("");
        navigate(`/admin/service_center/ticket/${ticketId}`);
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      context.notification.show(
        "Error creating ticket",
        "error",
        true,
        "toast",
        "x-circle"
      );
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    let ticketData = {
      status: "Active",
      ticketStatus: "Pending",
      agencyId: agencyInfo.agencyId,
      companyName: companyInfo.companyName || "",
      agencyName: agencyInfo.agencyName,
      assignedUserEmail: companyInfo.companyEmail || "",
      assignedAgentEmail: '',
      userId: companyInfo.companyId || "",
      submissionNumber: quoteNumberValue,
      quoteId: '',
      quote: selectedQuote,
    };

    try {
      setLoading(true);

      if (selectedQuote === "Cover Whale" && manualInputRequired && agencyInfo.agencyId) {
        const agencyData = await fetchAgencyData(agencyInfo.agencyId);

        if (!agencyData || !agencyData.owner_email) {
          setLoading(false);
          return;
        }

        ticketData.assignedAgentEmail = agencyData.owner_email;
        return await submitTicket(ticketData);
      }

      if (selectedQuote === "Cover Whale" && quoteNumberValue) {
        const response = await axios.get(`/api/quote/admin?page=1&search=${quoteNumberValue}`);
        const quotes = response.data.data.data.quotes;

        if (!quotes || quotes.length === 0) {
          const quoteData = await fetchQuoteFromCoverWhale(quoteNumberValue);

          if (!quoteData) {
            setManualInputRequired(true);
            setLoading(false);
            return;
          }

          let foundAgencyId = '';
          const agencyName = quoteData.retailAgent.agency_name;

          if (agencyName) {
            try {
              const matchingAgency = agenciesOptions.find(
                agency => agency.label.toLowerCase() === agencyName.toLowerCase()
              );

              if (matchingAgency) {
                foundAgencyId = matchingAgency.value;
              } else {
                const searchResponse = await axios
                  .get(
                    `/api/account/list/search?search=${encodeURIComponent(agencyName)}`
                  );
                const agencies = searchResponse.data.data.data;
                if (agencies && agencies.length > 0) {
                  foundAgencyId = agencies[0].id;
                } else {
                  console.warn(`Agency not found: ${agencyName}`);
                  setManualInputRequired(true);
                }
              }
            } catch (error) {
              console.error('Error finding agency:', error);
              setManualInputRequired(true);
            }
          } else {
            setManualInputRequired(true);
          }

          ticketData = {
            ...ticketData,
            companyName: '',
            assignedUserEmail: '',
            agencyId: foundAgencyId,
            agencyName: agencyName || '',
            assignedAgentEmail: quoteData.retailAgent.email || '',
          };

          if (!foundAgencyId) {
            setManualInputRequired(true);
          }
        } else {
          const validQuote = findMostRecentValidQuote(quotes);

          if (!validQuote) {
            context.notification.show(
              "No valid Cover Whale quote found for this submission number.",
              "error",
              true,
              "toast",
              "x-circle"
            );
            setManualInputRequired(true);
            setLoading(false);
            return;
          }

          ticketData = {
            ...ticketData,
            companyName: validQuote.company_name || "",
            assignedUserEmail: "",
            agencyId: validQuote.producer || validQuote.account_id,
            assignedAgentEmail: validQuote.agent_email || validQuote.quote?.retailAgent?.Email || "",
            agencyName: validQuote.account_name || "",
            quoteId: validQuote.id || "",
          };
        }
      }
      else if (
        (selectedQuote === "Progressive" || selectedQuote === "Nirvana")
        && agencyInfo.agencyId) {
        const agencyData = await fetchAgencyData(agencyInfo.agencyId);

        if (!agencyData || !agencyData.owner_email) {
          context.notification.show(
            "No valid agency data found.",
            "error",
            true,
            "toast",
            "x-circle"
          );
          setLoading(false);
          return;
        }

        ticketData = {
          ...ticketData,
          agencyId: agencyInfo.agencyId,
          companyName: companyInfo.companyName,
          agencyName: agencyInfo.agencyName,
          assignedUserEmail: companyInfo.companyEmail,
          assignedAgentEmail: agencyData.owner_email,
          userId: companyInfo.companyId,
        };
      }

      return await submitTicket(ticketData);
    } catch (error) {
      console.error('Error creating ticket:', error);
      context.notification.show(
        "There was an error processing your request",
        "error",
        true,
        "toast",
        "x-circle"
      );
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedQuote === "") {
      context.notification.show(
        "Please select a quote type",
        "error",
        true,
        "toast",
        "x-circle"
      );
      return;
    }

    if (!quoteNumberValue) {
      context.notification.show(
        "Please enter a submission number",
        "error",
        true,
        "toast",
        "x-circle"
      );
      return;
    }

    if ((selectedQuote === "Progressive" || selectedQuote === "Nirvana") && !agencyInfo.agencyId) {
      context.notification.show(
        "Please select an agency",
        "error",
        true,
        "toast",
        "x-circle"
      );
      return;
    }

    if (selectedQuote === "Cover Whale" && manualInputRequired && !agencyInfo.agencyId) {
      context.notification.show(
        "Please select an agency",
        "error",
        true,
        "toast",
        "x-circle"
      );
      return;
    }

    await createTicket();
  }

  return (
    <Fragment>
      {modal.name === "service_center_modal" && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div
                style={{
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: 24,
                  marginBottom: 10,
                  color: "black",
                }}
              >
                Create Ticket
              </div>
              <Formik
                initialValues={{
                  submissionNumber: '',
                  agency: '',
                  company: '',
                }}
                validationSchema={validationSchema}
              >
                <>
                  <Form
                    className={styles.form}
                  >
                    <div
                      style={{
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "black",
                        marginTop: 20,
                        marginBottom: 20,
                      }}
                    >
                      {selectedQuote === "" && (
                        <>
                          <div
                            style={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: 18,
                              marginBottom: 10
                            }}
                          >
                            Create a ticket to correct an issue with quote from
                          </div>
                          {quoteType.map((quote, index) => (
                            <button
                              key={index}
                              style={{
                                width: "100%",
                                padding: "10px 40px",
                                backgroundColor: "white",
                                border: "solid 1px #ddd",
                                borderRadius: "6px",
                                marginBottom: "6px",
                                cursor: "pointer",
                                fontStyle: "normal",
                                fontWeight: 500,
                                fontSize: 18,
                              }}
                              onClick={() => setSelectedQuote(quote)}
                            >
                              {quote}
                            </button>
                          ))}
                        </>
                      )}

                      {selectedQuote === "Cover Whale" && (
                        <>
                          <div
                            style={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: 18,
                            }}
                          >
                            Submission Number
                          </div>
                          <FieldInput
                            id="quote"
                            type="text"
                            placeholder="Enter submission number..."
                            handleChange={
                              (e) => setQuoteNumberValue(e.target.value)
                            }
                            value={quoteNumberValue}
                          />

                          {manualInputRequired && (
                            <>
                              <div
                                style={{
                                  fontStyle: "normal",
                                  fontWeight: 400,
                                  fontSize: 16,
                                  marginBottom: 15,
                                  color: "#666",
                                }}
                              >
                                We couldn't find data for this submission number.
                                Please provide the agency and company details manually.
                              </div>

                              <div style={{ marginTop: 15 }}>
                                <FieldInput
                                  id="agency_filter"
                                  type="search_select"
                                  label="*Agency"
                                  defaultValue={[]}
                                  handleChange={handleAgencyChange}
                                  options={agenciesOptions}
                                />
                              </div>
                              <div style={{ marginTop: 5 }}>
                                <FieldInput
                                  id="company_filter"
                                  type="search_select"
                                  label="Company"
                                  defaultValue={[]}
                                  handleChange={handleCompanyChange}
                                  options={companiesOptions}
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {(selectedQuote === "Progressive" || selectedQuote === "Nirvana") && (
                        <>
                          <div style={{ marginTop: 5 }}>
                            <FieldInput
                              id="agency_filter"
                              type="search_select"
                              label="*Agency"
                              defaultValue={[]}
                              handleChange={handleAgencyChange}
                              options={agenciesOptions}
                            />
                          </div>
                          <div style={{ marginTop: 5 }}>
                            <FieldInput
                              id="company_filter"
                              type="search_select"
                              label="Company"
                              defaultValue={[]}
                              handleChange={handleCompanyChange}
                              options={companiesOptions}
                            />
                          </div>
                          <div style={{ marginTop: 5 }}>
                            <FieldInput
                              id="quote"
                              type="text"
                              label="*Policy Number"
                              placeholder="Enter submission number..."
                              handleChange={
                                (e) => setQuoteNumberValue(e.target.value)
                              }
                              value={quoteNumberValue}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </Form>
                  <div
                    className={styles.footer}
                    style={{ justifyContent: "flex-end", }}
                  >
                    <div className={styles.buttons}>
                      <button
                        className={styles.cancel}
                        onClick={() => {
                          setModal("");
                          setSelectedQuote("");
                        }}
                      >
                        Cancel
                      </button>

                      {selectedQuote !== "" && (
                        <button
                          className={styles.save}
                          type="button"
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          Next
                        </button>
                      )}
                    </div>
                  </div>
                </>
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  )
}
